// Destination Country Step
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Image,
  Platform
} from "react-native";
import WebCountryFlag, { ReactCountryFlag } from 'react-country-flag';
import { loadCountries } from "../../../../../services/common.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import RadioSelectedIcon from "../../../../../../../../assets/images/Radio-Select-Button.png";
import FlatListDataComponent from "../../../common/FlatListDataComponent";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
countries.registerLocale(enLocale);

type ApiCountry = {
  id?: number;
  CountryCode: string;
  CountryName: string;
  IsActive: number | boolean;
};

type ChooseCountryStepProps = {
  onCountrySelected?: (code: string) => void;
};

export default function ChooseCountryStep({ onCountrySelected }: ChooseCountryStepProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [countriesData, setCountriesData] = useState<ApiCountry[]>([]);

  const setSelectedDestinationCountryCode = useSessionStore(
    (s: SessionState) => s.setSelectedDestinationCountryCode
  );
  const setSelectedDestinationCountryName = useSessionStore(
    (s: SessionState) => s.setSelectedDestinationCountryName
  );

  // Convert 2-letter ISO country code to emoji flag
  const countryCodeToEmoji = (code: string) => {
    if (!code) return "";
    const upper = code.toUpperCase();
    if (upper.length !== 2) return "";
    const base = 127397;
    return [...upper].map((c) => String.fromCodePoint(base + c.charCodeAt(0))).join("");
  };

  // Fetch countries from API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const result = await loadCountries();
        const apiCountries: ApiCountry[] = (result?.data ?? []).filter((c: ApiCountry) => {
          const isActive = typeof c.IsActive === "number" ? c.IsActive === 1 : !!c.IsActive;
          return isActive && !!c.CountryName && !!c.CountryCode;
        });
        setCountriesData(apiCountries);
      } catch (e) {
        console.error("Failed to load countries in ChooseCountryStep", e);
      }
    };
    fetchCountries();
  }, []);

  // Filter countries by search input
  const filteredCountries: ApiCountry[] = useMemo(() => {
    const q = search.toLowerCase();
    return countriesData.filter((c) => (c.CountryName || "").toLowerCase().includes(q));
  }, [countriesData, search]);

  const onSelectCountry = (item: ApiCountry) => {
    setSelectedCountry(item.CountryName);
    setSelectedDestinationCountryName(item.CountryName);
    setSelectedDestinationCountryCode(item.CountryCode);
    onCountrySelected?.(item.CountryCode);
  };

  return (
    <View style={styles.container}>
      <FlatListDataComponent
        title={t("COUNTRY_STEP_TITLE")}
        subTitle={t("COUNTRY_STEP_SUBTITLE")}
        searchPlaceHolder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
        searchValue={search}
        onSearchChangeText={setSearch}
        filteredData={filteredCountries}
        renderItem={(item: ApiCountry) => {
          const alpha2 = countries.alpha3ToAlpha2(item.CountryCode?.toUpperCase() ?? "") ?? "";

          return (
            <TouchableOpacity style={styles.countryRow} onPress={() => onSelectCountry(item)}>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                {Platform.OS === "web" ? (
                  <View
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 50,
                      overflow: "hidden",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 10,
                    }}
                  >
                    {alpha2 ? (
                      <ReactCountryFlag
                        countryCode={alpha2}
                        svg
                        style={{ width: 35, height: 35, borderRadius: 50 }}
                        aria-label={item.CountryName}
                      />
                    ) : (
                      <Text style={{ fontSize: 20 }}>🌍</Text>
                    )}
                  </View>
                ) : (
                  <Text style={styles.flag}>{countryCodeToEmoji(alpha2) || "🌍"}</Text>
                )}
                <Text style={styles.countryName}>{item.CountryName}</Text>
              </View>

              <View style={styles.radioButton}>
                {selectedCountry === item.CountryName && (
                  <Image
                    source={RadioSelectedIcon}
                    style={{ height: 20, width: 20 }}
                    resizeMode="contain"
                  />
                )}
              </View>
            </TouchableOpacity>
          );
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  countryRow: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    borderBottomWidth: 0,
    justifyContent: 'space-between'
  },
  flag: {
    fontSize: 22,
    marginRight: 12,
  },
  countryName: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 20,
    color: Colors.darkGrey,
    paddingLeft: 16
  },
  radioContainer: {
    marginLeft: 12,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#D1D5DB',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.radioButtonFilled
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#D1D5DB",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: '#D1D1D1'
  },
  radioOuterSelected: {
    borderColor: "#000",
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: "#000",
  },
  selectedText: {
    marginTop: 16,
    fontSize: 16,
    fontWeight: "500",
    color: "#111827",
  },
});
